import React, { useState } from 'react';
import I18n from 'i18n-js';
import { times } from 'lodash';
import { ExecutionResult } from 'graphql';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import {
  DeleteMyFavorite,
  UpsertMyFavorite,
  UndoDeleteMyFavorite,
  core_apimessages_FavoriteType,
  core_apimessages_Favorite } from '@reverbdotcom/commons/src/gql/graphql';
import { OverflowingRow } from '@reverbdotcom/commons';
import { homepageSearchRecommendationCount } from './homepage_search_recommendations';
import SearchRecommendation from './search_recommendation';
import { FavoritesHubUpsertFavoriteModal } from '../../favorites/favorite_modal';
import FavoriteToast from '@reverbdotcom/commons/src/components/favorite_toast';
import IndentedRow from '@reverbdotcom/commons/src/cms/components/indented_row';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import SignupSigninModal from '@reverbdotcom/commons/src/components/signup_signin_modal';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { FOLLOW_PROMPT_PARAM } from '../../shared/constants';

interface IProps {
  recentSearches: any;
  loading: boolean;
}

export function RecentSearchesRecommendations(props: IProps) {
  const { loading, recentSearches } = props;
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [hasApiError, setHasApiError] = useState<boolean>(false);
  const [searches, setSearches] = useState<any>(recentSearches || []);
  const defaultFavorite = {
    id: '',
    title: '',
    subtitle: '',
    feedEnabled: false,
    emailEnabled: false,
    queryParams: '',
  };
  const [selectedFavorite, setSelectedFavorite] = useState<core_apimessages_Favorite>(defaultFavorite);
  const user = useUser();

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: 'HomepageRecentSearches',
  }, true);

  React.useEffect(() => {
    setSearches(recentSearches);
  }, [loading]);

  const handleFavoriteUpdate = (key, val) => {
    let updatedFavorite = { ...selectedFavorite, [key]: val };
    if (key === 'feedEnabled' && !val) { updatedFavorite = { ...updatedFavorite, emailEnabled: false }; }
    setSelectedFavorite(updatedFavorite);
  };

  const handleModalOpen = (search) => {
    setSelectedFavorite({ title: search.query, feedEnabled: false, emailEnabled: false, queryParams: search.queryParams });
    setIsModalOpen(!isModalOpen);
  };

  const handleMutationResult = (result: ExecutionResult<UpsertMyFavorite.Mutation>) => {
    const favorite = result.data.upsertMyFavorite?.favorite;
    const index = searches.findIndex(s => s.queryParams === selectedFavorite.queryParams);
    handleUpdateSearches(favorite, index);
  };

  const handleFavoriteRemove = (index: number, result: ExecutionResult<DeleteMyFavorite.Mutation>) => {
    const favorite = result.data.deleteMyFavorite?.favorite;
    handleUpdateSearches(favorite, index);
  };

  const handleUndoFavoriteRemove = (result: ExecutionResult<UndoDeleteMyFavorite.Mutation>) => {
    const favorite = result.data.undoDeleteMyFavorite?.favorite;
    const index = searches.findIndex(s => s.favorite?.id === favorite.id);
    handleUpdateSearches(favorite, index);
  };

  const handleUpdateSearches = (favorite, index) => {
    setSelectedFavorite(favorite);
    searches[index] = { ...searches[index], favorite };
    setSearches(searches);

    setToastOpen(true);
  };

  const redirectTo = () => {
    if (selectedFavorite?.queryParams) {
      const queryParams = JSON.parse(selectedFavorite.queryParams);
      return Paths.marketplace.expand({ ...queryParams, [FOLLOW_PROMPT_PARAM]: true });
    } else {
      return '';
    }
  };

  if (!searches.length && !loading) { return null; }
  return (
    <>
      <IndentedRow
        title={I18n.t('discovery.homepage.searchRecommendations.recentSearches.title')}
        collection={
          <OverflowingRow title={I18n.t('discovery.homepage.searchRecommendations.recentSearches.title')}>
            <div className="d-grid gap-gutter grid-cols-2 tablet-grid-cols-2 mobile-d-block">
              { times(homepageSearchRecommendationCount(), (idx) => {
                return (
                  <div className="grid-col-span-1 pb-4" key={idx}>
                    <SearchRecommendation
                      search={searches[idx]}
                      idx={idx}
                      handleModalOpen={handleModalOpen}
                      handleFavoriteRemove={handleFavoriteRemove}
                    />
                  </div>
                );
              })}
            </div>
          </OverflowingRow>
        }
      />
      <SignupSigninModal
        onRequestClose={() => setIsModalOpen(false)}
        active={isModalOpen && user.loggedOut}
        message={I18n.t('discovery.searchSignup.fullLoginMessage')}
        redirectTo={redirectTo()}
        trackSource="FavoriteButton"
      />
      <FavoritesHubUpsertFavoriteModal
        isOpen={isModalOpen && !user.loggedOut}
        handleClose={() => setIsModalOpen(false)}
        handleUpdate={handleFavoriteUpdate}
        handleMutationResult={handleMutationResult}
        modalTitle={I18n.t('discovery.favorites.confirmationModal.updateTitle')}
        searchTitle={selectedFavorite.title}
        feedEnabled={selectedFavorite.feedEnabled}
        emailEnabled={selectedFavorite.emailEnabled}
        queryParams={selectedFavorite.queryParams}
      />
      <FavoriteToast
        favoriteId={selectedFavorite.id}
        isFavorited={selectedFavorite.favorited}
        favoriteType={core_apimessages_FavoriteType.SAVED_SEARCH}
        handleMutationResult={handleUndoFavoriteRemove}
        isOpen={toastOpen}
        handleToastClose={() => setToastOpen(false)}
        hasError={hasApiError}
        setHasError={setHasApiError}
      />
    </>
  );
}

export default RecentSearchesRecommendations;
